import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import lyrics from '../data/lyrics/its-ok33y';
import ProductCard from '../components/ProductCard';
import { DJController } from '../components/dj-kit';
import LRCLyricViewer from '../components/LRCLyricViewer';
import { rwdorable, BREAKPOINTS } from '../utils/rwdorable';
import { parseLRC } from '../utils/lrcParser';

const MusicContainer = styled.div`
    min-height: 100vh;
    padding: 100px 20px 60px 20px;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
    color: white;
    position: relative;
    max-width: 100vw;
    width: 100%;
    box-sizing: border-box;
    /* sticky 需要父容器没有 overflow-y: hidden/auto/scroll */

    @media (max-width: 768px) {
        padding: 100px 20px 60px 20px;
    }

    @media (max-width: 375px) {
        padding: 100px 15px 60px 15px;
    }
`;

const WaveformBackground = styled.div`
    position: fixed;
    bottom: 0;  
    max-width: 60vw;
    height: 100vh;
    z-index: 0;
    opacity: 0.5;
    pointer-events: ${props => props.isDraggable ? 'auto' : 'none'};
    overflow: hidden;
    cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};

    /* align center */
    left: 75%;
    transform: translateX(-50%);

    ${rwdorable('width', {
        base: 250,
        tablet: 200,
        sm: 100,
    })}
`;

const BeatGrid = styled.div`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    background-image: 
        /* 每四個拍一個紅色雙頭線（小節線）- 水平方向 */
        repeating-linear-gradient(
            to bottom,
            transparent 0px,
            transparent calc(4 * 20px - 1px),
            rgba(255, 0, 0, 1.0) calc(4 * 20px - 1px),
            rgba(255, 0, 0, 1.0) calc(4 * 20px + 1px),
            transparent calc(4 * 20px + 1px),
            transparent calc(4 * 20px)
        ),
        /* 每個拍一個細線 - 水平方向 */
        repeating-linear-gradient(
            to bottom,
            transparent 0px,
            transparent calc(20px - 0.5px),
            rgba(255, 255, 255, 0.2) calc(20px - 0.5px),
            rgba(255, 255, 255, 0.2) calc(20px + 0.5px),
            transparent calc(20px + 0.5px),
            transparent 20px
        );
    background-size: 100% 100%, 100% 100%;
    background-position: 0 0, 0 0;
    opacity: 0.6;
`;

const WaveformTrack = styled.div.attrs(props => {
    // 使用 attrs 来动态设置 transform，避免生成大量 CSS 类
    const offset = props.offset || 0;
    return {
        style: {
            transform: `translateY(${offset}px)`,
        },
    };
})`
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    height: 100vh;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    transition: none; /* 移除 transition，使用纯 JavaScript 控制以实现更流畅的实时响应 */
    will-change: transform; /* 提示浏览器优化 transform 性能 */
    user-select: none; /* 防止拖曳时选中文本 */
`;

const WaveformSVG = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
`;

const CueMarker = styled.div`
    position: absolute;
    top: ${props => props.top || '10%'};
    right: ${props => props.right || '10px'};
    width: 20px;
    height: 20px;
    background: rgba(0, 200, 255, 0.8);
    border: 2px solid rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: bold;
    color: white;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const ContentWrapper = styled.div`
    max-width: 1200px;
    margin: 0 auto 40px;
    position: relative;
    z-index: 1;
    width: 100%;
    box-sizing: border-box;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 50px;
`;

const Title = styled.h1`
    font-size: 3rem;
    margin-bottom: 15px;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.3rem;
    opacity: 0.9;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const Description = styled.p`
    font-size: 1rem;
    opacity: 0.8;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
`;

const DeerMemoImage = styled.img`
    max-width: 100%;
    height: auto;
    margin: 30px auto;
    display: block;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    opacity: 0.95;
    
    ${rwdorable('max-width', {
        base: 400,
        tablet: 300,
        sm: 250,
    })}
    
    @media (max-width: 768px) {
        margin: 20px auto;
    }
`;

const StickyContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    position: sticky;
    ${rwdorable('top', {
        base:  55,
        tablet: 55,
        sm: 55,
    })}
    z-index: 100;
    ${rwdorable('margin', {
        base: '420px 0 40px 0',
        tablet: '300px 0 40px 0',
        sm: '200px 0 40px 0',
    })}
    ${rwdorable('height', {
        base: 140,
        tablet: 112,
        sm: 80,
    })}
`;

const DJControllerWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 500px;
    height: 500px;
    /* 通过 transform 调整位置，让最后保留的高度显示在容器内 */
    transform: translateY(calc(-100% + ${props => props.finalVisibleHeight}px));
    pointer-events: auto;

    ${rwdorable('width', {
        base: 500,
        tablet: 400,
        sm: 300,
    })}
    ${rwdorable('height', {
        base: 500,
        tablet: 400,
        sm: 300,
    })}
`;

const PlatformSection = styled.section`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    ${rwdorable('margin-bottom', {
        base: '60px',
        tablet: '40px',
        sm: '0px',
    })}
`;

const SpotifyEmbed = styled.div`
    width: 100%;
    max-width: 100%;
    margin: 0 auto;

    iframe {
        border-radius: 12px;
        width: 100%;
        height: 352px;
        min-height: 352px;

        /* 参考 Spotify 的响应式设计 */
        @media (max-height: 351px) {
            height: 152px;
            min-height: 152px;
        }

        @media (max-height: 151px) {
            height: 80px;
            min-height: 80px;
        }

        @media (max-width: 509px) {
            /* 在较小宽度时保持默认高度，但允许 Spotify 内部调整 */
        }

        @media (max-height: 351px) and (max-width: 509px) {
            height: 152px;
            min-height: 152px;
        }

        @media (max-height: 151px) and (max-width: 509px) {
            height: 80px;
            min-height: 80px;
        }

        /* Mobile S 特定调整 */
        @media (max-width: 375px) {
            height: 300px;
            min-height: 300px;
        }

        @media (max-width: 375px) and (max-height: 351px) {
            height: 152px;
            min-height: 152px;
        }

        @media (max-width: 375px) and (max-height: 151px) {
            height: 80px;
            min-height: 80px;
        }
    }
`;

const SoundCloudEmbed = styled.div`
    width: 100%;
    max-width: 100%;
    margin: 0 auto;

    iframe {
        border-radius: 12px;
        width: 100%;
        min-height: 300px;
    }
`;

const LRCLyricViewerContainer = styled.div`
    text-align: center;
    margin-top: 0px;
`;

const MusicPage = () => {
    const { t } = useTranslation();
    // 常量定义
    const NAVBAR_HEIGHT = 45;
    const FINAL_VISIBLE_HEIGHT = 110; // 最后保留的高度
    
    // 歌曲路径
    const audioPath = '/audio/its-ok33y.mp3';
    const BASE_BPM = 95;
    
    // 状态管理（仅用于页面显示）
    const [, setIsPlaying] = useState(false);
    const [currentBPM, setCurrentBPM] = useState(BASE_BPM);
    const [animationDuration, setAnimationDuration] = useState((60 / BASE_BPM) * 20);
    const [currentTime, setCurrentTime] = useState(0); // 当前播放时间
    const [track1Offset, setTrack1Offset] = useState(0); // Track 1 的偏移量
    const [track2Offset, setTrack2Offset] = useState(0); // Track 2 的偏移量
    const [lrcData, setLrcData] = useState([]); // parsed LRC [{time, text}] for DJController scratch lyric lookup

    // Load + parse LRC file once so DJController can show time-corresponding lyrics during scratch
    useEffect(() => {
        fetch("/data/lyrics/It's ok33y - Deer Memo.lrc")
            .then(res => res.ok ? res.text() : Promise.reject(res.statusText))
            .then(text => {
                const { lyrics: parsed } = parseLRC(text);
                setLrcData(parsed);
            })
            .catch(err => console.warn('[MusicPage] Failed to load LRC for scratch lyrics:', err));
    }, []);

    // Refs
    const turntableRef = useRef(null);

    // 生成波形數據（垂直方向：時間軸從上到下，振幅從中心向左右）
    const generateWaveformData = (width, height, layer = 'blue') => {
        const segments = [];
        const segmentHeight = 8; // 每個波形段的高度
        const numSegments = Math.floor(height / segmentHeight);
        
        // 使用固定的隨機種子確保波形穩定，不同層使用不同種子
        let seed = layer === 'blue' ? 12345 : (layer === 'orange' ? 67890 : 11111);
        const random = () => {
            seed = (seed * 9301 + 49297) % 233280;
            return seed / 233280;
        };
        
        for (let i = 0; i < numSegments; i++) {
            const y = i * segmentHeight;
            const progress = i / numSegments;
            
            // 使用多個正弦波疊加創造更自然的波形
            const wave1 = Math.sin(progress * Math.PI * 8) * 0.3;
            const wave2 = Math.sin(progress * Math.PI * 15) * 0.2;
            const wave3 = Math.sin(progress * Math.PI * 23) * 0.15;
            const noise = random() * 0.3;
            const amplitude = 0.2 + noise + wave1 + wave2 + wave3;
            
            // 振幅變化，創造長短不一的波形（有些地方寬，有些地方窄）
            const variation = 0.3 + random() * 0.7;
            // X 軸：從中心線向左右延伸（振幅），不同層有不同的寬度
            const baseWidth = amplitude * variation * width * 0.4;
            // 白色層最窄，橘色層中等，藍色層最寬
            const widthMultiplier = layer === 'white' ? 0.6 : (layer === 'orange' ? 0.8 : 1.0);
            const offset = baseWidth * widthMultiplier;
            
            segments.push({
                y,
                height: segmentHeight,
                width: offset, // 從中心向左右延伸的寬度（總寬度的一半）
                layer
            });
        }
        
        return segments;
    };

    // 生成 CUE 標記位置（對齊右邊）
    const generateCueMarkers = () => {
        const markers = [];
        const numMarkers = 3 + Math.floor(Math.random() * 3); // 3-5 個標記
        for (let i = 0; i < numMarkers; i++) {
            markers.push({
                id: i,
                top: `${10 + Math.random() * 80}%`,
                right: '10px', // 對齊右邊
                label: String.fromCharCode(65 + i) // A, B, C...
            });
        }
        return markers;
    };

    const [cueMarkers] = useState(generateCueMarkers());
    // 波形應該是垂直的：寬度是振幅範圍，高度是時間軸
    const waveformWidth = 400; // 水平寬度（振幅範圍，從中心向左右）
    const segmentHeight = 2000; // 每個波形段的高度
    const numTracks = 2; // 創建2個波形段，用於無縫拼接
    const trackHeight = typeof window !== 'undefined' ? window.innerHeight : 1000; // 每個 track 的高度（100vh）

    // 使用 useMemo 緩存波形數據，生成多個段
    const waveformTracks = useMemo(() => {
        const tracks = [];
        for (let i = 0; i < numTracks; i++) {
            tracks.push({
                id: i,
                yOffset: i * segmentHeight, // 每個段的垂直偏移
                blueSegments: generateWaveformData(waveformWidth, segmentHeight, 'blue'),
                orangeSegments: generateWaveformData(waveformWidth, segmentHeight, 'orange'),
                whiteSegments: generateWaveformData(waveformWidth, segmentHeight, 'white')
            });
        }
        return tracks;
    }, []);

    // Spotify Artist ID from the embed URL
    const spotifyArtistId = '28SNO2Tc7dHMYRMBf4kVeS';
    const soundcloudUrl = 'https://soundcloud.com/r0akwqvpsvqr';

    // 从 DJController 获取状态的回调函数
    const currentTimeRef = useRef(0);
    const handleTimeUpdate = (newTime) => {
        currentTimeRef.current = newTime;
        setCurrentTime(newTime);
    };

    const handleBPMUpdate = (bpm) => {
        setCurrentBPM(bpm);
        setAnimationDuration((60 / bpm) * 20);
    };

    const handlePlayingStateUpdate = (playing) => {
        setIsPlaying(playing);
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = NAVBAR_HEIGHT + FINAL_VISIBLE_HEIGHT + 40;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const handleCueClick = () => {
        // 滚动到页面最上面
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // 根據螢幕寬度決定 DJController 的 size（與 rwdorable 斷點一致）
    const getDJSize = () => {
        if (typeof window === 'undefined') return 'large';
        const width = window.innerWidth;
        
        if (width <= BREAKPOINTS.sm) return 'small';      // <= 480px
        if (width <= BREAKPOINTS.tablet) return 'medium'; // <= 768px
        return 'large';                                    // > 768px
    };

    const [djSize, setDjSize] = useState(getDJSize);

    useEffect(() => {
        const handleResize = () => {
            setDjSize(getDJSize());
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 根據螢幕寬度決定 LRCLyricViewer 的 maxWidth（與 rwdorable 斷點一致）
    const getLyricMaxWidth = () => {
        if (typeof window === 'undefined') return '500px';
        const width = window.innerWidth;
        
        if (width <= BREAKPOINTS.sm) return '200px';      // <= 480px
        if (width <= BREAKPOINTS.tablet) return '400px';  // <= 768px
        return '500px';                                    // > 768px
    };

    const getLyricFontSize = () => {
        if (typeof window === 'undefined') return '1rem';
        const width = window.innerWidth;
        
        if (width <= BREAKPOINTS.sm) return '0.8rem';      // <= 480px
        if (width <= BREAKPOINTS.tablet) return '1rem';  // <= 768px
        return '1.2rem';                                    // > 768px
    };

    const [lyricMaxWidth, setLyricMaxWidth] = useState(getLyricMaxWidth);
    const [lyricFontSize, setLyricFontSize] = useState(getLyricFontSize);
    useEffect(() => {
        const handleResize = () => {
            setLyricMaxWidth(getLyricMaxWidth());
            setLyricFontSize(getLyricFontSize());
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 更新波形动画持续时间
    useEffect(() => {
        setAnimationDuration((60 / currentBPM) * 20);
    }, [currentBPM]);

    // 根据 currentTime 更新 Track 的 offset
    useEffect(() => {
        if (!animationDuration) return;

        const trackHeight = typeof window !== 'undefined' ? window.innerHeight : 1000;
        const vhToPx = trackHeight;
        
        // 计算进度（0 到 1）
        const progress = (currentTime % animationDuration) / animationDuration;

        // Track 1: 从 0 滚动到 -100vh，然后从 100vh 回到 0（循环）
        let track1OffsetValue;
        if (progress < 0.5) {
            track1OffsetValue = -vhToPx * (progress * 2);
        } else {
            track1OffsetValue = vhToPx * (1 - (progress - 0.5) * 2);
        }

        // Track 2: 从 100vh 滚动到 -100vh（循环）
        const track2OffsetValue = vhToPx - (vhToPx * 2 * progress);

        setTrack1Offset(track1OffsetValue);
        setTrack2Offset(track2OffsetValue);
    }, [currentTime, animationDuration]);


    // 生成波形矩形段（垂直方向，從中心線向左右延伸）
    const createWaveformRect = (segment) => {
        const centerX = waveformWidth / 2;
        // 所有波形都從中心向兩邊延伸
        return {
            x: centerX - segment.width, // 從中心向左延伸
            y: segment.y,
            width: segment.width * 2, // 總寬度是從中心到一邊的寬度的兩倍
            height: segment.height
        };
    };

    return (
        <MusicContainer>
            <WaveformBackground>
                <BeatGrid />
                {/* 第一個 track，從底部開始 */}
                <WaveformTrack offset={track1Offset}>
                    <WaveformSVG 
                        viewBox={`0 0 ${waveformWidth} ${segmentHeight * numTracks}`} 
                        preserveAspectRatio="none"
                        style={{ height: `${trackHeight}px` }}
                    >
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(0, 150, 255, 0.8)" />
                                <stop offset="50%" stopColor="rgba(0, 200, 255, 1)" />
                                <stop offset="100%" stopColor="rgba(0, 150, 255, 0.8)" />
                            </linearGradient>
                            <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(255, 150, 0, 0.8)" />
                                <stop offset="50%" stopColor="rgba(255, 200, 0, 1)" />
                                <stop offset="100%" stopColor="rgba(255, 150, 0, 0.8)" />
                            </linearGradient>
                            <linearGradient id="whiteGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(255, 255, 255, 0.6)" />
                                <stop offset="50%" stopColor="rgba(255, 255, 255, 0.9)" />
                                <stop offset="100%" stopColor="rgba(255, 255, 255, 0.6)" />
                            </linearGradient>
                        </defs>
                        {/* 渲染多個波形段，垂直拼接，分層：藍色底層，橘色中層，白色前層 */}
                        {waveformTracks.map(track => (
                            <g key={track.id}>
                                {/* 藍色波形段（底層） */}
                                {track.blueSegments.map((segment, index) => {
                                    const rect = createWaveformRect(segment);
                                    return (
                                        <rect
                                            key={`blue-${track.id}-${index}`}
                                            x={rect.x}
                                            y={rect.y + track.yOffset}
                                            width={rect.width}
                                            height={rect.height}
                                            fill="url(#blueGradient)"
                                            stroke="rgba(0, 200, 255, 0.9)"
                                            strokeWidth="0.5"
                                        />
                                    );
                                })}
                                {/* 橘色波形段（中層） */}
                                {track.orangeSegments.map((segment, index) => {
                                    const rect = createWaveformRect(segment);
                                    return (
                                        <rect
                                            key={`orange-${track.id}-${index}`}
                                            x={rect.x}
                                            y={rect.y + track.yOffset}
                                            width={rect.width}
                                            height={rect.height}
                                            fill="url(#orangeGradient)"
                                            stroke="rgba(255, 200, 0, 0.9)"
                                            strokeWidth="0.5"
                                        />
                                    );
                                })}
                                {/* 白色波形段（前層） */}
                                {track.whiteSegments.map((segment, index) => {
                                    const rect = createWaveformRect(segment);
                                    return (
                                        <rect
                                            key={`white-${track.id}-${index}`}
                                            x={rect.x}
                                            y={rect.y + track.yOffset}
                                            width={rect.width}
                                            height={rect.height}
                                            fill="url(#whiteGradient)"
                                            stroke="rgba(255, 255, 255, 0.9)"
                                            strokeWidth="0.5"
                                        />
                                    );
                                })}
                            </g>
                        ))}
                    </WaveformSVG>
                    {/* 只在第一個 track 中顯示 CUE markers，避免重複 */}
                    {cueMarkers.map(marker => (
                        <CueMarker key={marker.id} top={marker.top} right={marker.right}>
                            {marker.label}
                        </CueMarker>
                    ))}
                </WaveformTrack>
                {/* 第二個 track，初始位置接在第一段後面（在第一段上方），同時開始滾動 */}
                <WaveformTrack offset={track2Offset}>
                    <WaveformSVG 
                        viewBox={`0 0 ${waveformWidth} ${segmentHeight * numTracks}`} 
                        preserveAspectRatio="none"
                        style={{ height: `${trackHeight}px` }}
                    >
                        <defs>
                            <linearGradient id="blueGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(0, 150, 255, 0.8)" />
                                <stop offset="50%" stopColor="rgba(0, 200, 255, 1)" />
                                <stop offset="100%" stopColor="rgba(0, 150, 255, 0.8)" />
                            </linearGradient>
                            <linearGradient id="orangeGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(255, 150, 0, 0.8)" />
                                <stop offset="50%" stopColor="rgba(255, 200, 0, 1)" />
                                <stop offset="100%" stopColor="rgba(255, 150, 0, 0.8)" />
                            </linearGradient>
                            <linearGradient id="whiteGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(255, 255, 255, 0.6)" />
                                <stop offset="50%" stopColor="rgba(255, 255, 255, 0.9)" />
                                <stop offset="100%" stopColor="rgba(255, 255, 255, 0.6)" />
                            </linearGradient>
                        </defs>
                        {/* 渲染多個波形段，垂直拼接，分層：藍色底層，橘色中層，白色前層 */}
                        {waveformTracks.map(track => (
                            <g key={track.id}>
                                {/* 藍色波形段（底層） */}
                                {track.blueSegments.map((segment, index) => {
                                    const rect = createWaveformRect(segment);
                                    return (
                                        <rect
                                            key={`blue-${track.id}-${index}-2`}
                                            x={rect.x}
                                            y={rect.y + track.yOffset}
                                            width={rect.width}
                                            height={rect.height}
                                            fill="url(#blueGradient2)"
                                            stroke="rgba(0, 200, 255, 0.9)"
                                            strokeWidth="0.5"
                                        />
                                    );
                                })}
                                {/* 橘色波形段（中層） */}
                                {track.orangeSegments.map((segment, index) => {
                                    const rect = createWaveformRect(segment);
                                    return (
                                        <rect
                                            key={`orange-${track.id}-${index}-2`}
                                            x={rect.x}
                                            y={rect.y + track.yOffset}
                                            width={rect.width}
                                            height={rect.height}
                                            fill="url(#orangeGradient2)"
                                            stroke="rgba(255, 200, 0, 0.9)"
                                            strokeWidth="0.5"
                                        />
                                    );
                                })}
                                {/* 白色波形段（前層） */}
                                {track.whiteSegments.map((segment, index) => {
                                    const rect = createWaveformRect(segment);
                                    return (
                                        <rect
                                            key={`white-${track.id}-${index}-2`}
                                            x={rect.x}
                                            y={rect.y + track.yOffset}
                                            width={rect.width}
                                            height={rect.height}
                                            fill="url(#whiteGradient2)"
                                            stroke="rgba(255, 255, 255, 0.9)"
                                            strokeWidth="0.5"
                                        />
                                    );
                                })}
                            </g>
                        ))}
                    </WaveformSVG>
                    {/* 第二個 track 不顯示 CUE markers，避免重複 */}
                </WaveformTrack>
            </WaveformBackground>
            <ContentWrapper>
                <Header>
                    <Title>{t('music.title')}</Title>
                    <Subtitle>{t('music.subtitle')}</Subtitle>
                    <DeerMemoImage 
                        src="/images/deermemo.png" 
                        alt="Deer Memo"
                    />
                    <Description>
                        {t('music.description')}
                    </Description>
                </Header>

                <StickyContainer id="sticky-container" navbarHeight={NAVBAR_HEIGHT}>
                    <DJControllerWrapper ref={turntableRef} finalVisibleHeight={FINAL_VISIBLE_HEIGHT}>
                        <DJController
                            size={djSize}
                            audioPath={audioPath}
                            baseBPM={BASE_BPM}
                            lyrics={lyrics}
                            lrcData={lrcData}
                            onTimeUpdate={handleTimeUpdate}
                            onBPMUpdate={handleBPMUpdate}
                            onPlayingStateUpdate={handlePlayingStateUpdate}
                            onScrollToSection={scrollToSection}
                            onCueClick={handleCueClick}
                        />
                    </DJControllerWrapper>
                </StickyContainer>

                <PlatformSection id="spotify-section">
                    <ProductCard 
                        title={t('music.spotify')}
                        width={500}
                        height={'auto'}
                        maxWidth={314}
                    >
                        <SpotifyEmbed>
                            <iframe
                                data-testid="embed-iframe"
                                style={{ borderRadius: '12px' }}
                                src={`https://open.spotify.com/embed/artist/${spotifyArtistId}?utm_source=generator`}
                                width="100%"
                                height="352"
                                frameBorder="0"
                                allowFullScreen=""
                                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                loading="lazy"
                                title="Spotify Artist Player"
                            />
                        </SpotifyEmbed>
                    </ProductCard>
                </PlatformSection>

                <PlatformSection id="soundcloud-section">
                    <ProductCard 
                        title={t('music.soundcloud')}
                        width={500}
                        height={'auto'}
                        maxWidth={314}
                    >
                        <SoundCloudEmbed>
                            <iframe
                                width="100%"
                                height="300"
                                scrolling="no"
                                frameBorder="no"
                                allow="autoplay"
                                src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(soundcloudUrl)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
                                title="SoundCloud Player"
                            />
                        </SoundCloudEmbed>
                    </ProductCard>
                </PlatformSection>

                <LRCLyricViewerContainer id="lyrics-section">
                    <LRCLyricViewer 
                        maxWidth={lyricMaxWidth}
                        fontSize={lyricFontSize}
                        lrcPath="/data/lyrics/It's ok33y - Deer Memo.lrc"
                        currentTime={currentTime}
                    />
                </LRCLyricViewerContainer>
            </ContentWrapper>
        </MusicContainer>
    );
};

export default MusicPage;

